import React from "react";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { ReceiptIcon as Receipt, ArrowSquareOutIcon as ArrowSquareOut, ShieldCheckIcon as ShieldCheck, QuestionIcon as Question, InfoIcon as Info, CheckCircleIcon as CheckCircle, XIcon as X, WarningCircleIcon as WarningCircle, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Public seller-facing explainer for 1099-K and Stripe Connect taxes.
 *
 * The full audit (Iteration 139, see /app/memory/CHANGELOG.md) confirmed
 * that every dollar flowing to a seller on Armstrong K9 Registry routes
 * through Stripe Connect destination charges with
 * `transfer_data.destination` set to the seller's Connect account.
 * That makes **Stripe the Payment Settlement Entity (PSE)**, not the
 * platform. Stripe issues the 1099-K to the seller's TIN; Armstrong
 * never sees the SSN/EIN and never issues a Form 1099 on its own
 * behalf for any of these payouts.
 *
 * Surface goal: explain that plainly, in language a working trainer,
 * groomer, or breeder can act on without a CPA. Always link out to
 * the actual Stripe / IRS source — never restate.
 */

export default function HelpTaxes1099k() {
  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="help-1099k-page">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FBF7EC] border border-gold/40">
            <Receipt size={22} weight="duotone" className="text-gold-dark" />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold-dark">
              Tax help · For sellers
            </p>
            <h1 className="font-spectral font-light text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              1099-K and Stripe Connect, in plain English
            </h1>
          </div>
        </div>
        <p className="text-[14px] text-[#6B6B6B] mt-3 leading-relaxed">
          You&rsquo;re reading this because you sell something through
          Armstrong K9 Registry &mdash; dogs, training, grooming, walks,
          sitting, transport, food, merch. Here&rsquo;s exactly how the
          tax paperwork shakes out, who issues what, and what you should
          do this year.
        </p>

        {/* TL;DR card — the one thing most sellers actually want. */}
        <div
          className="mt-7 rounded-2xl border-2 border-gold/40 bg-[#FBF7EC] p-6"
          data-testid="help-1099k-tldr"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-2">
            TL;DR
          </p>
          <ul className="space-y-2 text-[14px] text-ink leading-relaxed">
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong>Stripe issues your 1099-K, not Armstrong.</strong> You&rsquo;ll download it from your Stripe Express dashboard in January.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong>Armstrong never sees your SSN or EIN.</strong> You gave it to Stripe during onboarding; we never touch it.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong>Federal threshold (TY2025): $5,000 in gross payouts.</strong> Several states are lower. Your state tax authority sets that piece.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong>1099-K reports GROSS, not your net.</strong> The platform fee and any refunds are deducted on your Schedule C, not on the 1099-K itself.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong>This page is not tax advice.</strong> If your situation is anything other than a sole proprietor with one payment processor, talk to a CPA.</span>
            </li>
          </ul>
        </div>

        {/* The structure: a Q&A list, because that's how sellers ask
            this question. Every answer keeps to the facts; we don't
            speculate about state thresholds we can't keep current. */}
        <Faq
          q="Who actually files my 1099-K?"
          testid="help-1099k-faq-issuer"
        >
          <p>
            <strong>Stripe</strong> does. Every payout from Armstrong K9
            Registry uses Stripe&rsquo;s &ldquo;destination charge&rdquo;
            pattern, which makes Stripe the legal{" "}
            <em>Payment Settlement Entity</em> on your sales &mdash; not
            Armstrong. That&rsquo;s the IRS&rsquo;s technical term for
            &ldquo;the party who has to file the 1099-K.&rdquo;
          </p>
          <p className="mt-2">
            In practice, that means in January you&rsquo;ll sign into
            your Stripe Express dashboard and download the form there.
            Armstrong will not mail you a 1099 for these payouts and
            does not file one on its own behalf for them.
          </p>
        </Faq>

        <Faq
          q="What's the dollar threshold this year?"
          testid="help-1099k-faq-threshold"
        >
          <p>
            <strong>$5,000 in gross payouts</strong> federally for
            TY2025 (the form you&rsquo;ll get in January 2026), per IRS
            Notice 2024-85. The IRS plans to lower this further over
            time, but the current $5K floor is what Stripe&rsquo;s
            issuance is keyed to.
          </p>
          <p className="mt-2">
            <strong>State thresholds are often lower</strong> &mdash;
            for instance California, Maryland, Massachusetts, New
            Jersey, Vermont, Virginia, and the District of Columbia
            have their own (lower) issuance rules. Stripe handles all
            of those automatically based on the address on your Connect
            account.
          </p>
        </Faq>

        <Faq
          q="If I'm under the threshold, do I still owe tax?"
          testid="help-1099k-faq-under-threshold"
        >
          <p>
            <strong>Yes.</strong> The 1099-K threshold is about <em>who
            sends you a form</em>, not about whether you owe tax. The
            IRS&rsquo;s long-standing rule is that all income from your
            business is reportable, whether you received a 1099 or
            not. If you grossed $4,000 of grooming through Armstrong K9
            Registry this year, you still report it on Schedule C even
            if Stripe doesn&rsquo;t issue you a 1099-K.
          </p>
        </Faq>

        <Faq
          q="What does the 1099-K actually show? Is it my net?"
          testid="help-1099k-faq-gross-vs-net"
        >
          <p>
            The 1099-K reports your <strong>GROSS</strong> &mdash; every
            dollar a buyer paid into your Connect account before any
            deductions. That means:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Refunds you issued <em>do not</em> reduce the gross figure on the 1099-K.</li>
            <li>The 12% (services), 10% (transport), 5% (merch), or 3% (storefront food) platform fee Armstrong took <em>does not</em> reduce the gross figure on the 1099-K.</li>
            <li>Sales tax Stripe Tax collected and remitted <em>is still</em> included in the gross figure.</li>
          </ul>
          <p className="mt-2">
            You account for refunds, platform fees, and any sales tax
            remitted to the state on <strong>your Schedule C</strong>{" "}
            (or 1120-S, 1065, etc.). The 1099-K is a starting line, not
            a finish line.
          </p>
        </Faq>

        <Faq
          q="Where do I download my 1099-K?"
          testid="help-1099k-faq-download"
        >
          <p>
            From your <strong>Stripe Express dashboard</strong>:
          </p>
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            <li>Sign in at{" "}
              <a
                href="https://connect.stripe.com/express_login"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crimson hover:underline inline-flex items-center gap-1 font-semibold"
              >
                connect.stripe.com/express_login <ArrowSquareOut size={11} weight="bold" />
              </a>
            </li>
            <li>Open the <strong>Tax forms</strong> tab.</li>
            <li>Download the PDF when it appears (Stripe issues by January 31 each year).</li>
          </ol>
          <p className="mt-2">
            Lost your Stripe login? Tap any &ldquo;Manage payouts on Stripe&rdquo;
            link inside your Armstrong dashboard &mdash; we&rsquo;ll
            generate a one-time login link to your Express account.
          </p>
        </Faq>

        <Faq
          q="Stripe is asking me to confirm my TIN. What do I do?"
          testid="help-1099k-faq-tin"
        >
          <p>
            If Stripe shows you a banner about verifying your tax ID,
            <strong> finish it as soon as you can</strong>. An unverified
            TIN can trigger 24% federal backup withholding on your
            payouts &mdash; Stripe holds it back, and you have to claim
            it later on your return.
          </p>
          <p className="mt-2">
            Click <strong>Verify TIN</strong> inside Express, enter the
            number exactly as it appears on your IRS letter (no
            spaces, no dashes), and submit. Stripe matches it against
            the IRS database in real time.
          </p>
        </Faq>

        <Faq
          q="I sell on multiple Armstrong personas — does that change anything?"
          testid="help-1099k-faq-multi-persona"
        >
          <p>
            <strong>No.</strong> If your Stripe Express account is the
            same across personas (typical), Stripe rolls every payout
            together into one 1099-K. So a grooming + transport +
            storefront merch combination shows up as a single annual
            figure, not three separate forms.
          </p>
        </Faq>

        <Faq
          q="Does Armstrong issue any 1099 itself?"
          testid="help-1099k-faq-armstrong-1099"
        >
          <p>
            <strong>Not on these payouts.</strong> Because every sale
            through Armstrong K9 Registry routes through Stripe Connect
            destination charges with Stripe as the Payment Settlement
            Entity, the 1099-K obligation falls on Stripe. We do not
            duplicate it.
          </p>
          <p className="mt-2">
            The narrow exceptions Armstrong does issue tax forms on:
            referral bonuses paid by ACH outside Stripe (rare, only on
            specific promo programs), and admin-paid contractor work
            (1099-NEC). Neither of those is a normal seller payout
            &mdash; if you&rsquo;re reading this page, neither applies
            to you.
          </p>
        </Faq>

        <Faq
          q="What about sales tax?"
          testid="help-1099k-faq-sales-tax"
        >
          <p>
            That&rsquo;s a separate topic from 1099-K, but the short
            version: depending on your trade and the state, Stripe Tax
            may calculate, collect, and remit sales tax for you
            automatically when you enable it. The setup walkthrough is
            on the{" "}
            <Link
              to="/help/stripe-tax-setup"
              className="text-crimson hover:underline font-semibold inline-flex items-center gap-1"
              data-testid="help-1099k-stripe-tax-link"
            >
              Stripe Tax setup page <ArrowRight size={11} weight="bold" />
            </Link>
            .
          </p>
        </Faq>

        {/* What this page is NOT — keep this LOUD. Tax advice is the
            #1 thing a venue platform should never improvise. */}
        <div
          className="mt-10 rounded-2xl border border-amber-300 bg-amber-50 p-6"
          data-testid="help-1099k-not-tax-advice"
        >
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={20} weight="fill" className="text-amber-700" />
            <p className="font-spectral text-lg text-amber-900">This is not tax advice.</p>
          </div>
          <p className="text-[13.5px] text-amber-900 leading-relaxed">
            Armstrong K9 Registry is a venue, not a tax preparer. This
            page summarizes how the payment plumbing works on this
            platform. It does not address your business structure,
            your state, your deductions, your prior-year losses, or
            anything else specific to your tax situation. If your
            situation is anything other than a sole proprietor with one
            payment processor and one state nexus, talk to a CPA.
          </p>
        </div>

        {/* External links — every claim above should be one tap from
            its real source. */}
        <div className="mt-8 rounded-xl border border-[#EAEAEA] bg-white p-6" data-testid="help-1099k-links">
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-3">
            Authoritative sources
          </p>
          <ul className="space-y-2 text-[13.5px]">
            <li>
              <a
                href="https://stripe.com/docs/connect/tax-reporting"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crimson hover:underline inline-flex items-center gap-1.5 font-semibold"
              >
                <ShieldCheck size={13} weight="duotone" />
                Stripe Connect tax reporting (official docs)
                <ArrowSquareOut size={11} weight="bold" />
              </a>
            </li>
            <li>
              <a
                href="https://www.irs.gov/businesses/understanding-your-form-1099-k"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crimson hover:underline inline-flex items-center gap-1.5 font-semibold"
              >
                <Info size={13} weight="duotone" />
                IRS: Understanding your Form 1099-K
                <ArrowSquareOut size={11} weight="bold" />
              </a>
            </li>
            <li>
              <a
                href="https://www.irs.gov/forms-pubs/about-schedule-c-form-1040"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crimson hover:underline inline-flex items-center gap-1.5 font-semibold"
              >
                <Info size={13} weight="duotone" />
                IRS: About Schedule C (where most of you report this)
                <ArrowSquareOut size={11} weight="bold" />
              </a>
            </li>
            <li>
              <a
                href="https://connect.stripe.com/express_login"
                target="_blank"
                rel="noopener noreferrer"
                className="text-crimson hover:underline inline-flex items-center gap-1.5 font-semibold"
              >
                <ArrowSquareOut size={13} weight="bold" />
                Open Stripe Express &rarr; Tax forms
                <ArrowSquareOut size={11} weight="bold" />
              </a>
            </li>
          </ul>
        </div>

        <p className="mt-10 mb-2 text-[12px] text-[#9A9A9A] leading-relaxed" data-testid="help-1099k-questions">
          Have a tax question that&rsquo;s specific to Armstrong K9 Registry&rsquo;s
          payout mechanics? Open a{" "}
          <Link to="/help" className="underline text-ink hover:text-crimson">
            help ticket
          </Link>
          . For tax questions that aren&rsquo;t platform-specific, please go
          to a CPA &mdash; we can&rsquo;t help with those and shouldn&rsquo;t
          pretend to.
        </p>
      </div>
    </AppShell>
  );
}

function Faq({ q, children, testid }) {
  return (
    <div className="mt-6 rounded-xl border border-[#EAEAEA] bg-white p-6" data-testid={testid}>
      <div className="flex items-start gap-3 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-ink text-white flex-shrink-0">
          <Question size={14} weight="bold" />
        </span>
        <h2 className="font-spectral text-lg text-ink leading-tight">{q}</h2>
      </div>
      <div className="text-[13.5px] text-[#3F3F3F] leading-relaxed pl-10">
        {children}
      </div>
    </div>
  );
}
